import { MeineFavoritenButton } from '../../../components/favoriten/Favoriten';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isOffeneAnfrage, trialRequestsApi, type TrialRequestHistoryItem, type TrialRequestItem } from '../../../lib/trialRequests';
import TrialWizardModal from '../../../components/trials/TrialWizardModal';
import { useStandort } from '../../../components/layouts/use-standort';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, MessageSquare, Phone, Clipboard, Search } from 'lucide-react';
import { buildTrialRequestContact } from '../../../lib/trialRequestContact';
import { formatDateTime } from '../../../lib/dateFormat';
import TrialRequestHideWizardModal from '../../../components/trialRequests/TrialRequestHideWizardModal';
import { api, communicationSettingsApi, type CtiProvider, type StandortCommunicationDto } from '../../../lib/api';
import TrialRequestGridCard from '../../../components/trialRequests/TrialRequestGridCard';
import MessageWizardModal, { type MessageChannel, type MessageRecipient } from '../../../components/communications/MessageWizardModal';
import TrialRequestMergeWizardModal from '../../../components/trialRequests/TrialRequestMergeWizardModal';
import { usePermission } from '../../../components/rbac/use-permission';

const TrialRequests: React.FC = () => {
  const navigate = useNavigate();
  const { selectedStandortId } = useStandort();
  // Endgültig löschen darf nur, wer das Recht dazu hat. Der Knopf stand bisher
  // bei allen — samt Sicherheitsabfrage — und scheiterte erst am Server (T-0026).
  const darfLoeschen = usePermission('anfragen.anfrage_loeschen');
  const darfFavorisieren = usePermission('anfragen.anfrage_als_favoriten_hinzufuegen');
  const [nurFavoriten, setNurFavoriten] = useState(false);
  // Ausgeblendete Anfragen ansehen und wieder einblenden (T-0063).
  const [zeigeAusgeblendete, setZeigeAusgeblendete] = useState(false);
  const darfAusblenden = usePermission('anfragen.anfrage_archivieren');
  const [items, setItems] = useState<TrialRequestItem[]>([]);
  const [zuLoeschen, setZuLoeschen] = useState<TrialRequestItem | null>(null);
  const [fachNameById, setFachNameById] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [selected, setSelected] = useState<TrialRequestItem | null>(null);
  const [openHistoryOverlay, setOpenHistoryOverlay] = useState(false);
  const [historyOverlayFor, setHistoryOverlayFor] = useState<TrialRequestItem | null>(null);
  const [historyOverlayItems, setHistoryOverlayItems] = useState<TrialRequestHistoryItem[]>([]);
  const [historyOverlayLoading, setHistoryOverlayLoading] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const [convertDefaults, setConvertDefaults] = useState<{ fachId?: number; teacherId?: number; roomId?: number; date?: string; startTime?: string; subprofileId?: string } | null>(null);
  const [openHide, setOpenHide] = useState(false);
  const [openMerge, setOpenMerge] = useState(false);
  const [mergeFor, setMergeFor] = useState<TrialRequestItem | null>(null);
  const [ctiProvider, setCtiProvider] = useState<CtiProvider>('none');
  const [commByStandort, setCommByStandort] = useState<Record<number, StandortCommunicationDto>>({});
  const [openMessageOverlay, setOpenMessageOverlay] = useState(false);
  const [messageOverlayFor, setMessageOverlayFor] = useState<TrialRequestItem | null>(null);

  const [messageOpen, setMessageOpen] = useState(false);
  const [messageRecipients, setMessageRecipients] = useState<MessageRecipient[]>([]);
  const [messageInitialChannel, setMessageInitialChannel] = useState<MessageChannel>('email');
  const [messageInitialDrafts, setMessageInitialDrafts] = useState<any>(undefined);
  const [messageVars, setMessageVars] = useState<Record<string, string>>({});

  const load = async () => {
    if (!selectedStandortId) return;
    setLoading(true);
    try {
      const data = await trialRequestsApi.list({
        standort_id: selectedStandortId,
        q: debouncedQ || undefined,
        with_existing_person: true,
        favoritesOnly: nurFavoriten || undefined,
        status: zeigeAusgeblendete ? 'ausgeblendet' : undefined,
      });
      // Mit dem Schalter kommen nur die ausgeblendeten, sonst nur die offenen.
      setItems(zeigeAusgeblendete ? (data || []) : (data || []).filter(isOffeneAnfrage));
    } catch (e: any) {
      toast.error(e?.message || 'Anfragen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadFaecher = async () => {
      try {
        const faecher = await api.get<Array<any>>('/api/faecher');
        if (cancelled) return;
        const map: Record<number, string> = {};
        (faecher || []).forEach((f: any) => {
          const id = Number(f?.id);
          const name = String(f?.name || '').trim();
          if (!Number.isNaN(id) && id > 0 && name) map[id] = name;
        });
        setFachNameById(map);
      } catch {
        // optional
      }
    };
    loadFaecher();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => globalThis.clearTimeout(t);
  }, [q]);

  useEffect(() => { void load(); }, [selectedStandortId, debouncedQ, nurFavoriten, zeigeAusgeblendete]);

  useEffect(() => {
    const loadComm = async () => {
      try {
        const [cfg, map] = await Promise.all([
          communicationSettingsApi.get(),
          communicationSettingsApi.getStandorteMap(),
        ]);
        setCtiProvider(cfg?.cti?.provider || 'none');
        const normalized: Record<number, StandortCommunicationDto> = {};
        Object.entries(map || {}).forEach(([k, v]) => {
          const id = Number(k);
          if (!Number.isNaN(id)) normalized[id] = v;
        });
        setCommByStandort(normalized);
      } catch {
        // Kommunikation ist optional für diese UI – stillschweigend
      }
    };
    loadComm();
  }, []);

  const loadHistoryOverlay = async (it: TrialRequestItem) => {
    setHistoryOverlayFor(it);
    setOpenHistoryOverlay(true);
    setHistoryOverlayLoading(true);
    try {
      const data = await trialRequestsApi.history.list(it.id);
      setHistoryOverlayItems(data);
    } catch (e: any) {
      toast.error(e?.message || 'Historie konnte nicht geladen werden');
      setHistoryOverlayItems([]);
    } finally {
      setHistoryOverlayLoading(false);
    }
  };

  const addHistoryForId = async (id: number, payload: { type: string; description: string; details?: string | null; meta?: Record<string, unknown> | null }) => {
    try {
      const created = await trialRequestsApi.history.create(id, {
        type: payload.type,
        description: payload.description,
        details: payload.details ?? null,
        meta: payload.meta ?? null,
      });

      // Kartenansicht sofort aktualisieren, damit "Ereignisse" ohne Reload sichtbar sind.
      setItems((prev) => prev.map((it) => (
        it.id === id
          ? {
              ...it,
              last_history_description: created.description || it.last_history_description,
              last_history_created_at: created.createdAt || it.last_history_created_at,
              last_history_details: created.details ?? null,
            }
          : it
      )));

      if (historyOverlayFor?.id === id) {
        setHistoryOverlayItems((prev) => [created, ...prev]);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Historien-Eintrag fehlgeschlagen');
    }
  };

  const iconForType = (t: string, meta?: Record<string, unknown> | null) => {
    // Versand aus dem Nachrichten-Assistenten: der Kanal steht im Beleg (T-0017).
    const kanal = String(meta?.channel || '').toLowerCase();
    const key = (t || '').toLowerCase().startsWith('communication_') && kanal ? kanal : (t || '').toLowerCase();
    if (key.includes('email') || key === 'mail') return <Mail className="h-4 w-4" />;
    if (key.includes('whatsapp') || key === 'wa') return <MessageSquare className="h-4 w-4" />;
    if (key.includes('sms')) return <MessageSquare className="h-4 w-4" />;
    if (key.includes('call')) return <Phone className="h-4 w-4" />;
    return <Clipboard className="h-4 w-4" />;
  };

  const isMergeRequired = (it: TrialRequestItem): boolean => {
    const hasLink = Boolean((it.wp_user_id && it.wp_user_id > 0) || (it.subprofile_id && String(it.subprofile_id).trim() !== ''));
    return it.existing_person_source === 'email_match' && !hasLink;
  };

  const canConvert = (it: TrialRequestItem): boolean => !isMergeRequired(it);

  // Der Dialog sucht den Kunden selbst; eine Vorwahl „verknüpfen oder neu" gab
  // es nur, solange man das vorher wissen musste.
  const openMergeFor = (it: TrialRequestItem) => {
    setMergeFor(it);
    setOpenMerge(true);
  };

  const openMessageFor = (it: TrialRequestItem) => {
    const contact = buildTrialRequestContact(it);
    const hasEmail = Boolean(contact.email);
    const hasPhone = Boolean(contact.phone);
    if (!hasEmail && !hasPhone) {
      toast.error('Keine E-Mail-Adresse oder Telefonnummer vorhanden');
      return;
    }
    const body = contact.emailBody || '';
    const escape = (s: string) =>
      s
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    const htmlTpl =
      '<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif; font-size:14px; line-height:1.5">'
      + escape(body).replaceAll('\n', '<br/>')
      + '</div>';

    setSelected(it);
    setMessageRecipients([
      {
        id: `trial_request:${it.id}`,
        type: 'trial_request',
        trialRequestId: it.id,
        name: contact.name || `${(it.vorname || '').trim()} ${(it.nachname || '').trim()}`.trim() || 'Empfänger',
        email: contact.email || null,
        phone: contact.phone || null,
      },
    ]);
    setMessageInitialChannel(hasEmail ? 'email' : 'sms');
    // Die gepflegte Vorlage arbeitet mit Platzhaltern; ohne Werte ging „Hallo
    // {{studentFirstName}}," wörtlich an den Interessenten raus (T-0019).
    setMessageVars({
      studentName: contact.name || '',
      studentFirstName: (it.vorname || '').trim(),
      studentLastName: (it.nachname || '').trim(),
      fachName: it.fach_id ? (fachNameById[it.fach_id] || '') : '',
      bevorzugteZeiten: (it.bevorzugte_zeiten || '').trim(),
      nachricht: (it.notizen || '').trim(),
    });
    setMessageInitialDrafts({
      email: { subjectTpl: contact.emailSubject || 'Probestunden-Anfrage – Rückmeldung', htmlTpl, textTpl: body },
      sms: { smsTpl: contact.smsBody || '' },
    });
    setMessageOpen(true);
  };

  const historyHoverText = (h: TrialRequestHistoryItem): string => {
    const meta = (h.meta && typeof h.meta === 'object') ? h.meta : null;
    if (!meta) return '';
    const isMessage = h.type === 'message_sent' || h.description === 'E-Mail gesendet' || h.description === 'SMS gesendet';
    if (!isMessage) return '';
    if (String(meta.channel || '') === 'email') {
      const subject = String(meta.subject || '').trim();
      const text = String(meta.text || '').trim();
      if (subject && text) return `${subject}\n${text}`;
      return subject || text;
    }
    if (String(meta.channel || '') === 'sms') {
      return String(meta.text || meta.preview || '').trim();
    }
    return '';
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Probestunden-Anfragen</h1>
        <p className="text-muted-foreground">Standortbezogene Anfragen sichten und umwandeln</p>
      </div>
      {/* Suche links, „Meine Favoriten“ direkt daneben (T-0014). Vorher lagen die
          Knöpfe in einem Block-Container und rutschten unter die Suche. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Suche (Name/E-Mail)" value={q} onChange={e => setQ(e.target.value)} className="pl-8 md:w-64" />
          </div>
          {darfFavorisieren ? (
            <MeineFavoritenButton aktiv={nurFavoriten} onChange={setNurFavoriten} />
          ) : null}
        </div>
        <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={zeigeAusgeblendete ? 'default' : 'outline'}
              aria-pressed={zeigeAusgeblendete}
              onClick={() => setZeigeAusgeblendete((an) => !an)}
              title="Ausgeblendete Anfragen ansehen und bei Bedarf wieder einblenden"
            >
              {zeigeAusgeblendete ? 'Offene Anfragen' : 'Ausgeblendete anzeigen'}
            </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="rounded-lg border p-6 text-sm text-muted-foreground">
              {zeigeAusgeblendete ? 'Keine ausgeblendeten Anfragen.' : 'Keine Anfragen gefunden.'}
            </div>
          ) : (
            items.map((it) => (
              <TrialRequestGridCard
                key={it.id}
                item={it}
                fachName={it.fach_id ? fachNameById[it.fach_id] : null}
                onOpenPersonAction={() => {
                  // Die Legacy-Kennung ist keine Adresse in der Personenverwaltung:
                  // Bei einem über die Website angelegten Konto ist sie eine
                  // erfundene Nummer, und die Seite lief in einen 404 (T-0050).
                  const personId = Number(it.existing_person_id || 0);
                  if (personId > 0 && it.existing_person_source === 'linked_account') {
                    navigate(`/dashboard/people/${personId}`);
                    return;
                  }
                  openMergeFor(it);
                }}
                onOpenMessage={() => {
                  setMessageOverlayFor(it);
                  setOpenMessageOverlay(true);
                }}
                onOpenHistory={() => { void loadHistoryOverlay(it); }}
                onCall={async () => {
                  const contact = buildTrialRequestContact(it);
                  if (!contact.tel) {
                    toast.error('Keine Telefonnummer vorhanden');
                    return;
                  }
                  const phone = (contact.phone || '').trim();
                  const standortId = it?.standort_id || selectedStandortId || null;
                  if (ctiProvider === 'nfon') {
                    const akz = standortId ? (commByStandort[Number(standortId)]?.nfonAkz || '').trim() : '';
                    if (!akz) { toast.error('NFON AKZ ist für diesen Standort nicht konfiguriert'); return; }
                    try {
                      const dial = `#${akz} ${phone}`;
                      await navigator.clipboard.writeText(dial);
                      toast.success('Wahlstring in Zwischenablage kopiert');
                      await addHistoryForId(it.id, { type: 'call', description: 'NFON Wahlstring kopiert', details: dial });
                    } catch {
                      toast.error('Kopieren in Zwischenablage fehlgeschlagen');
                    }
                    return;
                  }
                  await addHistoryForId(it.id, { type: 'call', description: 'Anruf gestartet' });
                  globalThis.location.href = contact.tel;
                }}
                onCallFailed={async () => {
                  await addHistoryForId(it.id, { type: 'call_failed', description: 'Anrufversuch ohne Erfolg' });
                }}
                onCommunicate={() => openMessageFor(it)}
                onOpenMerge={() => openMergeFor(it)}
                showMergeAction={isMergeRequired(it)}
                onConvert={() => {
                  if (!canConvert(it)) {
                    toast.error('Probestunde kann erst nach abgeschlossener Datensatz-Verknüpfung angelegt werden');
                    return;
                  }
                  setSelected(it);
                  setConvertDefaults({ fachId: it.fach_id || undefined });
                  setOpenConvert(true);
                }}
                onUnhide={
                  darfAusblenden
                    ? async () => {
                        try {
                          await trialRequestsApi.unhide(Number(it.id));
                          toast.success('Anfrage wieder eingeblendet');
                          await load();
                        } catch (e: any) {
                          toast.error(e?.message || 'Anfrage konnte nicht wieder eingeblendet werden');
                        }
                      }
                    : undefined
                }
                onHide={() => {
                  setSelected(it);
                  setOpenHide(true);
                }}
                onToggleFavorit={!darfFavorisieren ? undefined : async (favorit) => {
                  try {
                    const stand = await trialRequestsApi.setFavorit(it.id, favorit);
                    setItems((prev) =>
                      prev
                        .map((x) =>
                          x.id === it.id
                            ? { ...x, ist_favorit: stand.ist_favorit, favoriten: stand.favoriten }
                            : x
                        )
                        // Im Favoritenfilter verschwindet die Karte, sobald der Stern ausgeht.
                        .filter((x) => !nurFavoriten || x.ist_favorit)
                    );
                  } catch (e: any) {
                    toast.error(e?.message || 'Favorit konnte nicht gespeichert werden');
                  }
                }}
                onDelete={!darfLoeschen ? undefined : () => setZuLoeschen(it)}
                onAddQuickNote={async (text) => {
                  await addHistoryForId(it.id, { type: 'note', description: 'Kommentar hinzugefügt', details: text });
                }}
                canConvert={canConvert(it)}
              />
            ))
          )}
        </div>
      )}

      {/* History overlay (Mockup-style) */}
      <Dialog open={openHistoryOverlay} onOpenChange={setOpenHistoryOverlay}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Historie</DialogTitle>
            <DialogDescription>
              {historyOverlayFor ? `${historyOverlayFor.vorname} ${historyOverlayFor.nachname}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {(() => {
              if (historyOverlayLoading) {
                return <div className="text-sm text-muted-foreground">Lade…</div>;
              }
              if (historyOverlayItems.length === 0) {
                return <div className="text-sm text-muted-foreground">Noch keine Historie</div>;
              }
              return historyOverlayItems.map((h) => (
                <div key={h.id} className="rounded-md border p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <div className="text-muted-foreground">{iconForType(h.type, h.meta as Record<string, unknown> | null)}</div>
                      <div className="font-medium text-sm" title={historyHoverText(h) || undefined}>
                        {h.description}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">{formatDateTime(h.createdAt)}</div>
                      {(h.actorName || h.actorEmail || h.actorUserId) ? (
                        <div className="text-xs text-muted-foreground">
                          {h.actorName || h.actorEmail || `User #${h.actorUserId}`}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {h.details ? (
                    <div className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{h.details}</div>
                  ) : null}
                </div>
              ));
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenHistoryOverlay(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message overlay (preferred times / notes) */}
      <Dialog open={openMessageOverlay} onOpenChange={setOpenMessageOverlay}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Nachricht</DialogTitle>
            <DialogDescription>
              {messageOverlayFor ? `${messageOverlayFor.vorname} ${messageOverlayFor.nachname}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm whitespace-pre-wrap">
            {messageOverlayFor ? (
              (() => {
                const pref = (messageOverlayFor.bevorzugte_zeiten || '').trim();
                const notes = (messageOverlayFor.notizen || '').trim();
                const parts = [
                  pref ? `Bevorzugte Zeiten:\n${pref}` : '',
                  notes ? `Notizen:\n${notes}` : '',
                ].filter(Boolean);
                if (parts.length > 0) {
                  return parts.join('\n\n');
                }
                return 'Keine Nachricht vorhanden.';
              })()
            ) : (
              'Keine Nachricht vorhanden.'
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenMessageOverlay(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MessageWizardModal
        open={messageOpen}
        onOpenChange={(v) => {
          setMessageOpen(v);
          if (!v) {
            // refresh history overlays for a nicer UX
            if (historyOverlayFor?.id) {
              setHistoryOverlayLoading(true);
              trialRequestsApi.history
                .list(historyOverlayFor.id)
                .then((items) => setHistoryOverlayItems(items || []))
                .catch(() => setHistoryOverlayItems([]))
                .finally(() => setHistoryOverlayLoading(false));
            }
          }
        }}
        standortId={selectedStandortId ? Number(selectedStandortId) : 0}
        title="Nachricht senden"
        recipients={messageRecipients}
        context={selected?.id ? { entityType: 'trial_request', entityId: Number(selected.id) } : undefined}
        initialChannel={messageInitialChannel}
        templateKeys={{
          email: 'communications.manual.trial_request',
          sms: 'communications.manual.trial_request',
        }}
        vars={messageVars}
        initialDrafts={messageInitialDrafts}
        onSent={async () => {
          if (!selected?.id) return;
          // Live-Refresh der Historie direkt nach Versand.
          const fresh = await trialRequestsApi.history.list(selected.id);
          if (historyOverlayFor?.id === selected.id) {
            setHistoryOverlayItems(fresh || []);
          }
          setItems((prev) => prev.map((it) => (
            it.id === selected.id
              ? {
                  ...it,
                  last_history_description: fresh?.[0]?.description || it.last_history_description,
                  last_history_created_at: fresh?.[0]?.createdAt || it.last_history_created_at,
                  last_history_details: fresh?.[0] ? (fresh[0].details ?? null) : it.last_history_details,
                }
              : it
          )));
        }}
      />

      <TrialRequestMergeWizardModal
        open={openMerge}
        onOpenChange={setOpenMerge}
        request={mergeFor}
        onResolved={(updated) => {
          setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
          void load();
        }}
      />

      {selected ? (
        <TrialRequestHideWizardModal
          open={openHide}
          onOpenChange={setOpenHide}
          request={selected}
          onDone={async () => {
            setOpenHide(false);
            await load();
          }}
        />
      ) : null}

      <TrialWizardModal
        open={openConvert}
        onOpenChange={(v) => setOpenConvert(v)}
        trialRequestId={selected?.id}
        trialRequestPerson={{
          firstName: selected?.vorname,
          lastName: selected?.nachname,
          email: selected?.email,
          phone: selected?.telefon || undefined,
          wpUserId: selected?.wp_user_id ? Number(selected.wp_user_id) : undefined,
          personId: selected?.existing_person_id ? Number(selected.existing_person_id) : undefined,
          subprofileId: selected?.subprofile_id ? String(selected.subprofile_id) : undefined,
        }}
        defaultFachId={convertDefaults?.fachId}
        defaultTeacherId={convertDefaults?.teacherId}
        defaultRoomId={convertDefaults?.roomId}
        defaultDate={convertDefaults?.date}
        defaultStartTime={convertDefaults?.startTime}
        defaultSubprofileId={convertDefaults?.subprofileId}
        onCreated={async () => { setOpenConvert(false); await load(); }}
      />
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Anfrage von „${
          zuLoeschen
            ? `${zuLoeschen.vorname || ''} ${zuLoeschen.nachname || ''}`.trim() || zuLoeschen.email || `#${zuLoeschen.id}`
            : ''
        }“ löschen?`}
        beschreibung="Die Anfrage wird vollständig entfernt und verschwindet aus allen Statistiken. Das lässt sich nicht rückgängig machen. Der Grund wird im Protokoll festgehalten."
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (!zuLoeschen) return;
          const id = zuLoeschen.id;
          await trialRequestsApi.remove(id, grund);
          setItems((prev) => prev.filter((x) => x.id !== id));
          setZuLoeschen(null);
          toast.success('Anfrage gelöscht');
        }}
      />
    </div>
  );
};

export default TrialRequests;


